/**
 * Wire types for the admin API. These mirror the Pydantic models in `src/backend/app.py` and
 * `src/backend/state.py` field-for-field — keep the two in sync when either side changes.
 *
 * Dates cross the wire as ISO-8601 strings (Pydantic's default JSON encoding for `datetime`); we
 * keep them as `string` here rather than parsing to `Date` so every value round-trips through
 * PUT /api/admin/rules unchanged, and format them for display at the point of use.
 */

/** GET /api/branding response. No auth required — the login screen renders this before sign-in */
export interface Branding {
  name: string;
  logo_image: string;
  favicon: string;
  theme_color: string;
  css_file: string;
}

/**
 * GET /api/feedback-widget response. No auth required, for the same reason branding isn't.
 *
 * An empty `project_id` means load nothing — which is what a production deployment always
 * reports, whatever its configuration says (see `marker_io_project_id` in `backend/app.py`).
 */
export interface FeedbackWidget {
  project_id: string;
}

/** GET /api/session response. */
export interface SessionInfo {
  user_key: string;
  /** The signed-in address, for display. Empty for the break-glass account, which has none. */
  email: string;
  is_admin: boolean;
  /** True when signed in with the local break-glass account rather than the identity provider. */
  is_super_admin: boolean;
  /**
   * Whether this deployment bundles the chat front end *and* this caller may use it. False for the
   * break-glass account, which holds no IdP token and so cannot be permission-checked against the
   * file source — see `backend/chat.py`.
   */
  chat_available: boolean;
}

/** POST /api/auth/super-admin request body. */
export interface SuperAdminRequest {
  username: string;
  password: string;
}

/** A break-glass session. Valid against the admin API only. */
export interface SuperAdminSession {
  token: string;
  expires_at: number;
}

/** POST /api/admin/bootstrap request body. */
export interface BootstrapRequest {
  password: string;
}

/** One person authorised to administer this deployment. */
export interface Admin {
  user_key: string;
  email: string;
  display_name: string;
  added_by: string;
  added_at: string;
}

/** POST /api/admin/admins request body. Email is the normal identifier; the stable user_key is
 * bound by the backend on the person's first sign-in, so it is optional here. */
export interface AddAdminRequest {
  user_key?: string;
  email: string;
  display_name?: string;
}

/** One recorded administrative action, from GET /api/admin/audit. */
export interface AuditEntry {
  at: string;
  actor: string;
  action: string;
  target: string;
  succeeded: boolean;
  detail: Record<string, unknown>;
}

/**
 * GET/PUT /api/admin/rules body.
 *
 * `result_limit` caps how many hits a search returns and is enforced server-side; `results_shown`
 * is how many cards the chat draws before linking to the rest. The server rejects a body where
 * `results_shown` exceeds `result_limit`, so validate the pair before sending.
 */
export interface TuningRules {
  exclusion_globs: string[];
  augment_with_native_search: boolean;
  summary_enabled: boolean;
  result_limit: number;
  results_shown: number;
  updated_by: string;
  updated_at: string | null;
}

/** The largest value `result_limit` and `results_shown` may take. Mirrors MAX_RESULT_LIMIT. */
export const MAX_RESULT_LIMIT = 50;

/** One entry of GET /api/admin/domains. */
export interface DomainCandidate {
  domain: string;
  label: string;
  file_count: number;
  detail: Record<string, unknown>;
}

/**
 * Per-domain index state. `system_never_indexed`, `domain_never_indexed`, and `indexing` mean
 * "nothing was searched", not "nothing matched" — and must be rendered as informational states,
 * never as an empty table. See `nothingToSearch` below.
 */
export type IndexState =
  "system_never_indexed" | "domain_never_indexed" | "indexing" | "stale" | "partial" | "ready";

/**
 * How much of one domain has been indexed, and how fresh it is. Carried by the admin status screen
 * and by every `search_files` payload alike.
 */
export interface IndexStatus {
  state: IndexState;
  notice: string;
  files_indexed: number;
  files_total: number;
  last_indexed_at: string | null;
  /**
   * True when nothing was searched at all — a never-indexed or still-building domain. Sent by the
   * server (a Pydantic computed field), so this file does not keep its own copy of which states
   * qualify; a copy would be one deploy away from disagreeing with the server that decides.
   *
   * Rendering an empty result as "no files matched" when this is true tells the user something
   * false about their own files. Show the `notice` instead.
   */
  nothing_to_search: boolean;
}

/** One domain's coverage row on GET /api/admin/status. */
export interface DomainCoverage {
  domain: string;
  label: string;
  files_indexed: number;
  status: IndexStatus;
}

/** GET /api/admin/status response. */
export interface CoverageReport {
  files_total: number;
  domains: DomainCoverage[];
  generated_at: string | null;
}

/** One entry of GET /api/admin/sources. Never carries a credential value, only its presence. */
export interface SourceInfo {
  name: string;
  is_default: boolean;
  configured: boolean;
}

/** GET /api/admin/features. Read before the nav renders, so it can omit tabs that do nothing. */
export interface Features {
  scope_selection_enabled: boolean;
  /** False means this deployment never starts runs by itself, whatever the saved schedule says. */
  schedule_enabled: boolean;
}

/** One site or shared drive an administrator may approve for indexing. */
export interface ScopeCandidate {
  key: string;
  label: string;
  /** A web URL or similar, to tell two same-named sites apart. Display only. */
  detail: string;
  approved: boolean;
  /**
   * Approved, but discovery no longer returns it — a deleted site, or one the credential lost
   * sight of. Render it as an explanation, never drop it: a silently vanishing row is how an
   * admin loses track of why a site stopped updating.
   */
  missing: boolean;
}

/** GET /api/admin/sources/{source}/scopes. */
export interface ScopeSelection {
  source_name: string;
  /** What this source calls a scope ("site", "shared drive"). Use it in headings and prose. */
  scope_noun: string;
  enabled: boolean;
  /** Non-empty means discovery failed: only the already-approved rows are trustworthy. */
  discovery_error: string;
  scopes: ScopeCandidate[];
  updated_by: string;
  updated_at: string | null;
}

/** One approved scope, as returned by PUT /api/admin/sources/{source}/scopes. */
export interface ApprovedScope {
  source_name: string;
  key: string;
  label: string;
  approved_by: string;
  approved_at: string | null;
}

/**
 * How the last indexing run ended, or that it did not: `interrupted` means the run stopped
 * without reporting a result (a server restart or crash) and must be shown as neither a success
 * nor a failure — see `notice` on `RunStatus`, which carries the explanation in that case.
 */
export type RunState = "running" | "succeeded" | "failed" | "interrupted";

/** What an indexing run does. Mirrors the indexer CLI's own options. */
export type RunMode = "incremental" | "full" | "summarize";

/** The last known state of indexing, from GET /api/admin/runs and POST /api/admin/runs. */
export interface RunStatus {
  state: RunState;
  mode: RunMode | null;
  source: string;
  started_at: string | null;
  finished_at: string | null;
  exit_code: number | null;
  pid: number | null;
  started_by: string;
  log_path: string;
  /**
   * The actionable explanation, whenever there is one (e.g. exit code 2 means a configuration
   * problem and that nothing was indexed). Render this whenever it is non-empty, regardless of
   * `state`.
   */
  notice: string;
  /** How far along, while running. See `RunProgress`. */
  progress: RunProgress | null;
}

/**
 * How far a running indexing run has got. Present only while `state === "running"` — a finished
 * run's bar would be a stale artefact of wherever it stopped, which reads as progress.
 */
export interface RunProgress {
  container_label: string;
  containers_done: number;
  containers_total: number;
  /** Every file the walk has settled: processed plus skipped-as-unchanged. */
  files_done: number;
  files_total: number;
  /** The skipped share of `files_done` — already indexed by an earlier run. */
  files_unchanged: number;
  inserted: number;
  updated: number;
  deleted: number;
  updated_at: string | null;
}

/** GET /api/admin/runs response: the last known run, plus enough log to see why it ended so. */
export interface RunReport {
  status: RunStatus;
  log_tail: string[];
}

/** POST /api/admin/runs request body. */
export interface StartRunRequest {
  mode: RunMode;
  source?: string;
}

/**
 * When the indexer runs by itself, from GET/PUT /api/admin/schedule.
 *
 * `days` are 0 = Monday … 6 = Sunday and `hours` are 0–23, read in `timezone` (an IANA name). The
 * server rejects an enabled schedule with no day or no hour, because it would read as "on" and
 * never fire.
 */
export interface IndexSchedule {
  enabled: boolean;
  days: number[];
  hours: number[];
  timezone: string;
  mode: RunMode;
  source: string;
  updated_by: string;
  updated_at: string | null;
}

/** GET/PUT /api/admin/schedule response: the saved schedule, and when it will next fire. */
export interface ScheduleReport {
  schedule: IndexSchedule;
  /** Null when the schedule is disabled — there is no next run to name. */
  next_run_at: string | null;
}

// -- chat -----------------------------------------------------------------------------------
// These mirror the MCP surface's own published models in `src/mcps/models.py`, because the chat
// stream forwards tool payloads verbatim rather than reshaping them. Keep the two in sync.

/** One file that matched, from `search_files`. Everything a card needs, without a second call. */
export interface FileHit {
  file_id: string;
  name: string;
  path: string;
  source: string;
  /** Which domain the file lives in. A search need not name one, so a result set can span several. */
  domain: string;
  extension: string;
  size: number;
  last_modified: string | null;
  summary: string;
  summary_truncated: boolean;
  why_matched: string;
  other_versions_suppressed: number;
  score: number;
  web_url: string;
}

/** What the permission gate did. `hidden_count` is results withheld from *this* caller. */
export interface PermissionSummary {
  state: string;
  hidden_count: number;
  notice: string;
}

/**
 * The `search_files` payload, forwarded as the `search` stream event.
 *
 * Three distinct reasons the list can be incomplete, each with its own field — render whichever
 * apply, and never let one stand in for another: `index_status.nothing_to_search` (nothing was
 * searched), `permissions.hidden_count` (matches withheld from this user), and `results_truncated`
 * (the search filled its limit and stopped looking).
 */
export interface SearchResponse {
  hits: FileHit[];
  /** The domains this search was narrowed to. Empty means the whole corpus was searched. */
  domain_restriction: string[];
  files_searched_count: number;
  index_status: IndexStatus;
  permissions: PermissionSummary;
  /** The cap this search ran under — the lower of the caller's request and the admin's setting. */
  result_limit_applied: number;
  /** The ranked set filled its cap, so more files may match. How many more is not knowable. */
  results_truncated: boolean;
}

/** One candidate answer to "which client or project did you mean?". */
export interface DomainMatch {
  domain_restriction: string;
  label: string;
  file_count: number;
}

/** The `resolve_domain` payload, forwarded as the `domains` stream event. */
export interface DomainResolution {
  query: string;
  candidates: DomainMatch[];
  notice: string;
}

/**
 * GET /chat/api/settings — the display half of the tuning rules, readable by any signed-in user.
 * The admin route that owns these values requires an administrator, which a chat user is not.
 */
export interface ChatSettings {
  results_shown: number;
  result_limit: number;
}

/** One restored turn from GET /chat/api/history. */
export interface ChatMessage {
  role: "user" | "assistant";
  text: string;
}

/** POST /chat/api/feedback request body. */
export interface ChatFeedbackRequest {
  context: string;
  rating?: number | null;
  comment?: string;
}
